import type { Station } from '@hcc/types';

/**
 * Canonical asset categories per station. Drives the combobox in the
 * AssetForm so coordinators don't end up with "Laptop", "laptops" and
 * "Notebook" all meaning the same thing — which would also break the
 * category-aware field mapping in lib/kitItemFields.ts.
 *
 * The list is suggestion-only (rendered into a <datalist>): operators
 * can still type a custom value for one-off items, and the AssetsList
 * shows whatever string is stored. Anything that matches one of these
 * exactly will pick up the correct field labels in the issue drawer.
 */

export const ASSET_CATEGORIES: Record<Station, readonly string[]> = {
  IT: [
    'Laptop',
    'Desktop',
    'Monitor',
    'Phone',
    'Tablet',
    'SIM',
    'Headset',
    'Dock',
    'Webcam',
    'Charger',
    'Cable',
  ],
  PPE: [
    'Boots',
    'Hi-vis',
    'Helmet',
    'Gloves',
    'Trousers',
    'Jacket',
    'Eye protection',
    'Ear protection',
    'Mask',
  ],
  Tools: ['Drill', 'Hand tools', 'Ladder', 'Toolbox', 'Power tool'],
  Fleet: ['Van', 'Car', 'Truck', 'Trailer'],
  DBSHR: ['Document'],
};

/**
 * The identifier fields the asset register can capture. These map to keys
 * inside the asset's free-form `metadata` JSON.
 */
export const ASSET_IDENTIFIER_FIELDS = [
  { key: 'serial', label: 'Serial number', placeholder: 'e.g. SN-1234-5678' },
  { key: 'imei', label: 'IMEI', placeholder: 'phones / SIMs' },
  { key: 'mac_address', label: 'MAC address', placeholder: 'aa:bb:cc:...' },
  { key: 'registration', label: 'Registration', placeholder: 'AB23 XYZ (vehicles)' },
] as const;

export type AssetIdentifierKey = (typeof ASSET_IDENTIFIER_FIELDS)[number]['key'];

// Which identifier keys actually make sense per canonical category
// (keyed lowercased, matching lib/kitItemFields.ts). A van has a
// registration, not a MAC; a charger has neither. Anything not listed
// falls through to the station default below.
const CATEGORY_IDENTIFIERS: Record<string, readonly AssetIdentifierKey[]> = {
  laptop: ['serial', 'mac_address'],
  desktop: ['serial', 'mac_address'],
  monitor: ['serial'],
  tablet: ['serial', 'imei'],
  phone: ['imei', 'serial'],
  sim: ['imei'],
  headset: ['serial'],
  dock: ['serial', 'mac_address'],
  webcam: ['serial'],
  charger: [],
  cable: [],
  van: ['registration'],
  car: ['registration'],
  truck: ['registration'],
  trailer: ['registration'],
  drill: ['serial'],
  'hand tools': ['serial'],
  ladder: ['serial'],
  toolbox: ['serial'],
  'power tool': ['serial'],
  // PPE / uniform consumables and documents carry no tracked identifier.
  boots: [],
  'hi-vis': [],
  helmet: [],
  gloves: [],
  trousers: [],
  jacket: [],
  'eye protection': [],
  'ear protection': [],
  mask: [],
  document: [],
};

const STATION_IDENTIFIER_DEFAULTS: Record<Station, readonly AssetIdentifierKey[]> = {
  IT: ['serial'],
  PPE: [],
  Tools: ['serial'],
  Fleet: ['registration'],
  DBSHR: [],
};

/**
 * The identifier keys worth showing for a given station + category, so the
 * register doesn't ask for an IMEI on a hard hat or a registration on a
 * laptop. Unknown categories fall back to the station default. The form
 * still offers a "show all" escape hatch for one-offs.
 */
export function identifierFieldsFor(
  station: Station,
  category?: string | null,
): readonly AssetIdentifierKey[] {
  if (category) {
    const key = category.trim().toLowerCase();
    const byCategory = CATEGORY_IDENTIFIERS[key];
    if (byCategory) return byCategory;
  }
  return STATION_IDENTIFIER_DEFAULTS[station];
}
